"use client";

import React, { useState } from "react";
import { Check } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const plans = [{
  id: "essential",
  name: "CNCWise® Subscription",
  description: "Designed for shops of all sizes.",
  monthlyPrice: 349,
  annualPrice: 319,
  isPopular: false,
  buttonText: "Sign up",
  buttonVariant: "outline" as const,
  href: "https://app.cncwise.com/signup"
}];
const planFeatures = [{
  planId: "essential",
  feature: "Ai Technician - Unlimited Access"
}, {
  planId: "essential",
  feature: "Preventative Maintenance Tracking"
}, {
  planId: "essential",
  feature: "25% Electronic Component Repair Discount"
}, {
  planId: "essential",
  feature: "Unlimited Email Support"
}];
export default function SubscriptionPlans() {
  const [isAnnual, setIsAnnual] = useState(true);
  return <section id="subscription-plans" className="bg-muted/30 dark:bg-muted/10 py-24 sm:py-32">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl mb-4">Simple Pricing. Unlimited Solutions.</h2>
          <p className="text-lg text-muted-foreground mb-8">The average CNC service call in the United States costs <b>$1,325</b> in labor alone. The average cost for an electronic replacement component is <b>$6,745</b>. The average wait to get a technician on site is <b>three days</b>. You can't afford <i data-ast-id="f:/src/pages/Home/sections/SubscriptionPlans.tsx|sig:pdosyt" data-ast-start="1756" data-ast-end="1936" style={{
            fontWeight: 'bold'
          }}>not </i>to subscribe.</p>

          <div className="flex items-center justify-center gap-4">
            <span className={`text-sm font-medium ${!isAnnual ? "text-foreground" : "text-muted-foreground"}`}>
              Monthly
            </span>
            <Switch checked={isAnnual} onCheckedChange={setIsAnnual} aria-label="Toggle annual billing" />
            <span className={`text-sm font-medium flex items-center gap-2 ${isAnnual ? "text-foreground" : "text-muted-foreground"}`}>
              Annually
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5">
                Save 10%
              </Badge>
            </span>
          </div>
        </div>

        <div className="flex justify-center max-w-md mx-auto">
          {plans.map((plan, index) => {
          const features = planFeatures.filter(f => f.planId === plan.id);
          const price = isAnnual ? plan.annualPrice : plan.monthlyPrice;
          return <div key={plan.id} data-index={index} className={`relative w-full ${plan.isPopular ? "md:-mt-8 md:mb-8 z-10" : "z-0"}`}>
                <Card className={`h-full flex flex-col transition-all duration-200 ${plan.isPopular ? "border-primary shadow-xl md:scale-105 bg-card" : "border-border/50 shadow-sm hover:shadow-md bg-card/50 backdrop-blur-sm"}`}>
                  {plan.isPopular && <div className="absolute -top-4 left-0 right-0 flex justify-center">
                      <Badge className="bg-primary text-primary-foreground px-3 py-1 text-sm font-semibold uppercase tracking-wider shadow-sm">
                        CNCWise Choice
                      </Badge>
                    </div>}

                  <CardHeader className="text-center pb-2 pt-8">
                    <CardTitle className="text-2xl">{plan.name}</CardTitle>
                    <CardDescription className="min-h-[40px] mt-2">
                      {plan.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="flex-grow text-center pt-4 pb-8">
                    <div className="mb-6 flex items-baseline justify-center gap-1">
                      <span className="text-5xl font-bold tracking-tight text-foreground">
                        ${Math.round(price)}
                      </span>
                      <span className="text-muted-foreground font-medium">
                        /mo
                      </span>
                    </div>
                    {isAnnual && <p className="text-sm text-muted-foreground mb-6">
                        Billed ${Math.round(price * 12)} annually
                      </p>}
                    {!isAnnual && <p className="text-sm text-transparent mb-6 select-none">
                        Spacer
                      </p>}

                    <ul className="space-y-3 text-left mt-8">
                      {features.map((feature, fIndex) => <li key={fIndex} data-index={fIndex} className="flex items-start gap-3">
                          <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                          <span className="text-sm text-muted-foreground">
                            {feature.feature}
                          </span>
                        </li>)}
                    </ul>
                  </CardContent>

                  <CardFooter className="pt-4 pb-8">
                    <Button asChild variant={plan.buttonVariant} className={`w-full ${plan.isPopular ? "h-12 text-base" : "h-10"}`}>
                      <Link to={plan.href}>{plan.buttonText}</Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}